import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { Link, useInRouterContext } from "react-router-dom";
import { Icon } from "@uzverse/ui";
import { isLandmark } from "../bounds.js";
import {
  compassMarks,
  headingDeg,
  mapToWorld,
  minimapData,
  playerPose,
  worldToMap,
} from "../hud-math.js";
import type { CompassBuilding, MinimapData } from "../hud-math.js";
import { useWorldT } from "../i18n.js";
import { effectiveLayout, layoutStore, useLayout } from "../layout.js";
import { playUi, setSoundEnabled, soundEnabled } from "../sfx.js";
import { useWorldState } from "../store.js";
import type { WorldStore } from "../store.js";
import { useNavState } from "./nav.js";
import type { NavStore } from "./nav.js";

/**
 * How long the HUD waits before it dims: the help line (4 s) and the settings pills (6 s). Browser
 * automation never auto-hides (tests would race the timers) unless a page sets `__uzHudIdle`.
 */
export function hudIdleMs(kind: "help" | "controls"): number | null {
  const override = (globalThis as { __uzHudIdle?: number }).__uzHudIdle;
  if (typeof override === "number") return override;
  if (typeof navigator !== "undefined" && navigator.webdriver) return null;
  return kind === "help" ? 4000 : 6000;
}

/** True after `ms` without input (mouse, touch, keys, wheel); any input wakes it. Null: never idle. */
export function useIdle(ms: number | null): boolean {
  const [idle, setIdle] = useState(false);
  useEffect(() => {
    if (ms === null) {
      setIdle(false);
      return;
    }
    let timer: ReturnType<typeof setTimeout> | undefined;
    let last = 0;
    const arm = () => {
      clearTimeout(timer);
      timer = setTimeout(() => setIdle(true), ms);
    };
    const wake = () => {
      const now = performance.now();
      setIdle((was) => (was ? false : was));
      if (now - last < 150) return; // pointermove floods: re-arm at most ~6 times a second
      last = now;
      arm();
    };
    const events = ["pointermove", "pointerdown", "keydown", "wheel", "touchstart"] as const;
    for (const e of events) window.addEventListener(e, wake, { passive: true });
    arm();
    return () => {
      clearTimeout(timer);
      for (const e of events) window.removeEventListener(e, wake);
    };
  }, [ms]);
  return idle;
}

/** True on phone-width screens, where the settings open as a bottom sheet only when asked. */
export function usePhone(): boolean {
  const query = "(max-width: 699px)";
  const [phone, setPhone] = useState(() => globalThis.matchMedia?.(query).matches ?? false);
  useEffect(() => {
    const mq = globalThis.matchMedia?.(query);
    if (!mq) return;
    const on = () => setPhone(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return phone;
}

/** The scene's named buildings for the compass, refreshed when the scene changes. */
function useBuildings(store: WorldStore, rev: number, t: ReturnType<typeof useWorldT>) {
  return useMemo<CompassBuilding[]>(() => {
    void rev;
    const out: CompassBuilding[] = [];
    for (const n of store.nodes.values()) {
      if (n.parentId !== null || n.kind !== "building") continue;
      out.push({
        id: n.id,
        label: isLandmark(n) ? t("panel.landmarkTitle") : (n.label ?? ""),
        x: n.transform.position[0],
        z: n.transform.position[2],
      });
    }
    return out;
  }, [store, rev, t]);
}

const COMPASS_W = 320;

/** The compass strip at the top: N/E/S/W, ticks and the names of the buildings close by. */
export function Compass({ store, nav }: { store: WorldStore; nav: NavStore }) {
  const t = useWorldT();
  const rev = useWorldState(store).rev;
  const buildings = useBuildings(store, rev, t);
  const [view, setView] = useState({ heading: 0, key: "" });
  const [marks, setMarks] = useState<ReturnType<typeof compassMarks>>([]);
  const names = useMemo(
    () => ({
      N: t("game.north"),
      E: t("game.east"),
      S: t("game.south"),
      W: t("game.west"),
    }),
    [t],
  );
  useEffect(() => {
    // 8 Hz is plenty for a strip; the heading is read from the mutable pose, not from React.
    const tick = () => {
      if (document.hidden) return;
      const pose = playerPose(nav);
      const heading = Math.round(headingDeg(pose?.yaw ?? 0));
      const next = compassMarks(heading, pose, buildings, { cardinals: names });
      const key = `${heading}|${next.map((m) => `${m.key}${Math.round(m.offset)}`).join(",")}`;
      setView((v) => (v.key === key ? v : { heading, key }));
      setMarks(next);
    };
    tick();
    const id = setInterval(tick, 125);
    return () => clearInterval(id);
  }, [nav, buildings, names]);
  const ppd = COMPASS_W / 180;
  return (
    <div
      className="world-compass"
      data-testid="world-compass"
      data-heading={view.heading}
      role="img"
      aria-label={`${t("game.compass")}: ${view.heading}°`}
    >
      <span className="world-compass-needle" aria-hidden="true" />
      {marks.map((m) => (
        <span
          key={m.key}
          className={`world-compass-mark is-${m.kind}`}
          style={{ left: `calc(50% + ${(m.offset * ppd).toFixed(1)}px)` }}
        >
          {m.label}
        </span>
      ))}
    </div>
  );
}

const MAP_SIZE = 160;

/** The minimap (top right): parcels, buildings and the player; click to fly there in orbit mode. */
export function Minimap({ store, nav }: { store: WorldStore; nav: NavStore }) {
  const t = useWorldT();
  const canvas = useRef<HTMLCanvasElement>(null);
  const rev = useWorldState(store).rev;
  const data = useRef<MinimapData | null>(null);
  const seenRev = useRef(-1);
  const orbit = useNavState(nav).mode === "orbit";
  const dpr = typeof devicePixelRatio === "number" ? Math.min(2, Math.max(1, devicePixelRatio)) : 1;

  const draw = useCallback(() => {
    const el = canvas.current;
    const ctx = el?.getContext("2d");
    if (!el || !ctx || document.hidden) return;
    if (seenRev.current !== rev || !data.current) {
      data.current = minimapData(store.nodes.values());
      seenRev.current = rev;
    }
    const d = data.current;
    const css = getComputedStyle(el);
    const color = (name: string, fallback: string) => css.getPropertyValue(name).trim() || fallback;
    const accent = color("--accent", "#5e6ad2");
    const text = color("--text", "#ddd");
    const border = color("--border-strong", "#888");
    const soft = color("--accent-soft", "rgba(94,106,210,.2)");
    const muted = color("--muted", "#888");
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, MAP_SIZE, MAP_SIZE);
    for (const p of d.parcels) {
      const [x0, y0] = worldToMap(d.bounds, MAP_SIZE, p.minX, p.minZ);
      const [x1, y1] = worldToMap(d.bounds, MAP_SIZE, p.maxX, p.maxZ);
      ctx.fillStyle = soft;
      ctx.strokeStyle = border;
      ctx.lineWidth = 1;
      ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
      ctx.strokeRect(x0 + 0.5, y0 + 0.5, x1 - x0 - 1, y1 - y0 - 1);
    }
    const selected = store.getState().selected?.id;
    for (const b of d.buildings) {
      const [cx, cy] = worldToMap(d.bounds, MAP_SIZE, b.x, b.z);
      const w = Math.max(3, (b.w / (d.bounds.maxX - d.bounds.minX)) * MAP_SIZE);
      const h = Math.max(3, (b.d / (d.bounds.maxZ - d.bounds.minZ)) * MAP_SIZE);
      ctx.fillStyle = b.id === selected ? accent : b.landmark ? text : muted;
      ctx.fillRect(cx - w / 2, cy - h / 2, w, h);
    }
    const pose = playerPose(nav);
    if (pose) {
      const [px, py] = worldToMap(d.bounds, MAP_SIZE, pose.x, pose.z);
      const a = -pose.yaw; // clockwise from north, in screen space
      ctx.save();
      ctx.translate(
        Math.min(MAP_SIZE - 4, Math.max(4, px)),
        Math.min(MAP_SIZE - 4, Math.max(4, py)),
      );
      ctx.rotate(a);
      ctx.fillStyle = accent;
      ctx.strokeStyle = text;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(0, -7);
      ctx.lineTo(5, 5);
      ctx.lineTo(0, 2);
      ctx.lineTo(-5, 5);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }
  }, [store, nav, rev, dpr]);

  useEffect(() => {
    draw();
    const id = setInterval(draw, 250); // 4 fps: a map needs no more
    return () => clearInterval(id);
  }, [draw]);

  const fly = (e: ReactPointerEvent<HTMLCanvasElement>) => {
    const d = data.current;
    if (!d || nav.getState().mode !== "orbit" || nav.getState().inside) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const [x, z] = mapToWorld(
      d.bounds,
      MAP_SIZE,
      ((e.clientX - rect.left) / rect.width) * MAP_SIZE,
      ((e.clientY - rect.top) / rect.height) * MAP_SIZE,
    );
    const pose = nav.orbitPose;
    const off = pose
      ? [
          pose.position[0] - pose.target[0],
          pose.position[1] - pose.target[1],
          pose.position[2] - pose.target[2],
        ]
      : [0, 55, 70];
    nav.requestFlight([x, 0, z], [x + off[0]!, off[1]!, z + off[2]!]);
    playUi("tick");
  };
  return (
    <div className="world-minimap" data-testid="world-minimap">
      <canvas
        ref={canvas}
        width={MAP_SIZE * dpr}
        height={MAP_SIZE * dpr}
        style={{ width: MAP_SIZE, height: MAP_SIZE }}
        className={orbit ? "is-clickable" : undefined}
        role="img"
        aria-label={orbit ? `${t("game.minimap")}. ${t("game.minimapHint")}` : t("game.minimap")}
        title={orbit ? t("game.minimapHint") : undefined}
        onPointerDown={fly}
      />
    </div>
  );
}

/** A dot in the middle of the view while walking in first person. */
export function Crosshair() {
  return <span className="world-crosshair" data-testid="world-crosshair" aria-hidden="true" />;
}

function BrandLink() {
  const { t } = useTranslation("web");
  const tw = useWorldT();
  const inRouter = useInRouterContext();
  const body = (
    <>
      <span className="world-chip-mark" aria-hidden="true">
        U
      </span>
      <span className="world-chip-name">{t("app.name", { ns: "common" })}</span>
    </>
  );
  return inRouter ? (
    <Link
      to="/"
      className="world-chip-link"
      data-testid="world-back"
      aria-label={tw("game.backToApp")}
      title={tw("game.backToApp")}
    >
      {body}
    </Link>
  ) : (
    <a href="/" className="world-chip-link" aria-label={tw("game.backToApp")}>
      {body}
    </a>
  );
}

/** Top left: the menu drawer, back to the app (logo), the details panel and fullscreen. */
export function GameCorner() {
  const t = useWorldT();
  const ls = useLayout();
  const immersive = effectiveLayout(ls) === "immersive";
  const full = ls.fs !== "off";
  return (
    <div className="world-corner" data-testid="world-corner">
      <button
        type="button"
        className="world-glass-btn"
        data-testid="world-menu"
        aria-label={t("game.menu")}
        aria-expanded={ls.navOpen}
        title={t("game.menu")}
        onClick={() => {
          layoutStore.dispatch({ type: "toggleNav" });
          playUi(ls.navOpen ? "down" : "up");
        }}
      >
        <Icon name="menu" size={18} />
      </button>
      <BrandLink />
      <button
        type="button"
        className="world-glass-btn is-text"
        data-testid="world-panel-toggle"
        aria-pressed={immersive ? ls.panelOpen : false}
        title={t("game.panelTitle")}
        onClick={() => {
          layoutStore.dispatch({ type: "togglePanel" });
          playUi(ls.panelOpen ? "down" : "up");
        }}
      >
        <Icon name="panelRight" size={16} />
        <span>{t("game.panel")}</span>
        <kbd>Tab</kbd>
      </button>
      <button
        type="button"
        className="world-glass-btn"
        data-testid="world-fullscreen"
        aria-pressed={full}
        aria-label={full ? t("game.exitFullscreen") : t("game.fullscreen")}
        title={full ? t("game.exitFullscreen") : t("game.fullscreenTitle")}
        onClick={() => {
          void layoutStore.toggleFullscreen();
          playUi("tick");
        }}
      >
        <span aria-hidden="true">{full ? "⤡" : "⛶"}</span>
      </button>
    </div>
  );
}

/** The layout and sound switches that live in the settings pill. */
export function GameSwitches() {
  const t = useWorldT();
  const ls = useLayout();
  const [sound, setSound] = useState(soundEnabled);
  const docked = ls.layout === "docked";
  return (
    <div className="world-switches" role="group" aria-label={t("game.settings")}>
      <button
        type="button"
        data-testid="world-layout-toggle"
        aria-pressed={docked}
        title={t("game.layout")}
        disabled={ls.fs !== "off"}
        onClick={() =>
          layoutStore.dispatch({ type: "setLayout", layout: docked ? "immersive" : "docked" })
        }
      >
        {t("game.layout")}: {docked ? t("game.layoutDocked") : t("game.layoutImmersive")}
      </button>
      <button
        type="button"
        data-testid="world-sound-toggle"
        aria-pressed={sound}
        onClick={() => {
          const next = !sound;
          setSoundEnabled(next);
          setSound(next);
          playUi("up");
        }}
      >
        {t("game.sound")}
      </button>
    </div>
  );
}
